import React from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useTranslation } from '../context/Internationalization';
import { ACCOUNT_SETTINGS_ROUTE, LOGIN_ROUTE, REGISTRATION_ROUTE, SUPER_ADMIN_CLUBS_ROUTE, SUPER_ADMIN_IMPERSONATE_ROUTE, SUPER_ADMIN_ROLES_ROUTE, SUPER_ADMIN_HELP_LINKS_ROUTE } from '../routes';
import { readRegistrationProgress } from '../pages/Registration/registrationProgress';
import { hasFirebaseAction, isClublessSuperAdmin } from '../auth/consoleSession';
import FullPageLoader from './FullPageLoader';
import AuthLayout from '../pages/Login/AuthLayout';

const GLOBAL_ADMIN_ROUTES = [
  SUPER_ADMIN_CLUBS_ROUTE, SUPER_ADMIN_IMPERSONATE_ROUTE, SUPER_ADMIN_ROLES_ROUTE, SUPER_ADMIN_HELP_LINKS_ROUTE, ACCOUNT_SETTINGS_ROUTE,
];

export function SessionError() {
  const auth = useAuthContext();
  const { t } = useTranslation();
  return <AuthLayout><Stack spacing={3}>
    <Alert severity="error">{t('registration_session_error')}</Alert>
    <Button onClick={() => auth.refreshSession({ force: true }).catch(() => {})}>{t('registration_retry')}</Button>
    <Button variant="outlined" onClick={auth.logout}>{t('registration_change_account')}</Button>
  </Stack></AuthLayout>;
}

export function SessionAccessDenied() {
  const auth = useAuthContext();
  const { t } = useTranslation();
  return <AuthLayout><Stack spacing={3}>
    <Alert severity="info">{t('registration_manager_access_required')}</Alert>
    <Button onClick={auth.logout}>{t('registration_change_account')}</Button>
  </Stack></AuthLayout>;
}

export default function SessionBoundary({ children }) {
  const auth = useAuthContext();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  if (pathname === LOGIN_ROUTE) return children;
  // The outer account-link handler owns its actions; ordinary links reach Login
  // even when opened on a nested route or before Firebase restores a session.
  if (hasFirebaseAction()) return <Navigate to={LOGIN_ROUTE + window.location.search} replace />;
  if (auth.sessionStatus === 'loading') return <FullPageLoader message={t('signing_in')} />;
  if (auth.sessionStatus === 'error' || auth.sessionStatus === 'callback') return <SessionError />;
  if (auth.sessionStatus === 'signed-out') return <Navigate to={LOGIN_ROUTE} replace />;
  if (auth.session?.kind === 'restricted') return <SessionAccessDenied />;
  // A global administrator does not need a club. Keep club pages (including
  // direct URLs) unmounted until the effective user has a club context.
  if (isClublessSuperAdmin(auth.authState?.user)) {
    return GLOBAL_ADMIN_ROUTES.includes(pathname.replace(/\/+$/, ''))
      ? children : <Navigate to={SUPER_ADMIN_CLUBS_ROUTE} replace />;
  }
  const pending = !auth.authState?.impersonate?._id && readRegistrationProgress(auth.firebaseUser?.uid);
  if ((auth.session?.kind === 'onboarding' || pending) && pathname !== REGISTRATION_ROUTE) {
    return <Navigate to={REGISTRATION_ROUTE} replace />;
  }
  return children;
}
